import React, { useState } from 'react'
import classnames from 'classnames/bind'
import {
  Text,
  Icon,
  CardContainer,
  LabelValue,
  Divider,
  Statistic,
  InfoPill,
  Modal,
  Button,
  MarkdownRenderer,
  coreConstants,
} from '@adc/ui-morpheus'
import type { UserProfile } from '../../types/userInsights'
import { EM_DASH } from '../../constants/statics'
import { HELP_CONTENT } from './helpContent'
import styles from './styles.scss'

const cx = classnames.bind(styles)

export interface UserProfileCardProps {
  user: UserProfile
  embed?: boolean
  collapsible?: boolean
  collapsed?: boolean
  onToggle?: () => void
}

interface ActivityRow {
  label: string
  value: string
}

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const formatCurrency = (amount: number | null): string => {
  if (amount === null) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount)
}

const formatCount = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—'
  return String(value)
}

const ActivitySection: React.FC<{
  icon: string
  title: string
  rows: ActivityRow[]
}> = ({ icon, title, rows }) => (
  <div className={cx('activity-section')}>
    <div className={cx('activity-header')}>
      <Icon name={icon} iconStyle='solid' color='gray' />
      <Text size='large' weight='medium' color='grayDark'>
        {title}
      </Text>
    </div>
    <Divider fitted className={cx('divider-accent')} />
    {rows.map(row => (
      <div key={row.label} className={cx('activity-row')}>
        <Text size='tiny' color='gray'>
          {row.label}
        </Text>
        <Text size='tiny' weight='medium' color='grayDark'>
          {row.value}
        </Text>
      </div>
    ))}
  </div>
)

const UserProfileCard: React.FC<UserProfileCardProps> = ({
  user,
  embed = true,
  collapsible = false,
  collapsed = false,
  onToggle,
}) => {
  const [helpOpen, setHelpOpen] = useState(false)

  const initials = `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`
  const displayName = embed
    ? `${user.first_name} ${user.last_name}`
    : 'User Profile'

  const isSuspended = user.status === 'SUSPENDED'
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const banCount = user.banned_listing_count ?? 0
  const banLabel = banCount === 1 ? '1 listing' : `${banCount} listings`
  const statusPills = (
    <>
      {isSuspended && (
        <InfoPill
          elmId='user_account_suspended_pill'
          iconName='circle-exclamation'
          content='Account suspended'
          size='small'
          foregroundColor={coreConstants.ColorNames.Error}
          backgroundColor={coreConstants.ColorNames.ErrorExtraLight}
          style={{ alignSelf: 'center' }}
        />
      )}
      {banCount > 0 && (
        <InfoPill
          elmId='user_banned_from_bidding_pill'
          iconName='ban'
          content={`Bidding banned (${banLabel})`}
          size='small'
          foregroundColor={coreConstants.ColorNames.Caution}
          backgroundColor={coreConstants.ColorNames.CautionExtraLight}
          style={{ alignSelf: 'center' }}
        />
      )}
    </>
  )

  const liveRows: ActivityRow[] = [
    {
      label: 'Last registration',
      value: formatDate(user.live_activity.last_registration),
    },
    { label: 'Last bid', value: formatDate(user.live_activity.last_bid) },
    {
      label: 'Purchases total',
      value: formatCount(user.live_activity.purchases_total),
    },
  ]

  const onlineRows: ActivityRow[] = [
    {
      label: 'Last registration',
      value: formatDate(user.online_activity.last_registration),
    },
    { label: 'Last bid', value: formatDate(user.online_activity.last_bid) },
    {
      label: 'Purchases total',
      value: formatCount(user.online_activity.purchases_total),
    },
  ]

  const bidToCloseLeftRows: ActivityRow[] = [
    {
      label: 'Online bids',
      value: formatCount(user.bidding_activity.online_bids),
    },
    {
      label: 'Quality bids',
      value: formatCount(user.bidding_activity.quality_bids),
    },
    {
      label: 'Wallet funds',
      value: formatCurrency(user.bidding_activity.wallet_funds),
    },
  ]

  const bidToCloseRightRows: ActivityRow[] = [
    {
      label: 'Contracts in progress',
      value: formatCount(user.closing_activity.contracts_in_progress),
    },
    {
      label: 'Avg win \u2192 close*',
      value:
        user.closing_activity.avg_win_to_close_days !== null
          ? `${user.closing_activity.avg_win_to_close_days} days`
          : EM_DASH,
    },
    { label: 'Fallouts', value: formatCount(user.closing_activity.fallouts) },
  ]

  const remoteBidLeftRows: ActivityRow[] = [
    {
      label: 'Remote Approved',
      value: user.remote_approved ? 'Yes' : 'No',
    },
  ]

  const remoteBidRightRows: ActivityRow[] = [
    {
      label: 'Remote Bid Available',
      value: formatCurrency(user.bidding_activity.remote_bid_available),
    },
  ]

  const infoItems = [
    { label: 'Experience', value: user.experience },
    { label: 'Signup', value: formatDate(user.signup_date) },
    { label: 'Last Purchase', value: formatDate(user.last_purchase_date) },
  ]

  return (
    <CardContainer
      dropShadow='none'
      padding='none'
      borderStyle='line'
      borderColor={coreConstants.ColorNames.GrayLight}
    >
      {/* Header */}
      <div
        className={cx('header', { clickable: collapsible })}
        onClick={collapsible ? onToggle : undefined}
        role={collapsible ? 'button' : undefined}
        aria-expanded={collapsible ? !collapsed : undefined}
      >
        <div className={cx('header-left')}>
          <div className={cx('avatar')}>
            <Text size='tiny' weight='medium' color='white'>
              {initials}
            </Text>
          </div>
          <Text size='large' weight='medium' color='grayDark'>
            {displayName}
          </Text>
          {statusPills}
        </div>
        {collapsible && (
          <Icon
            name={collapsed ? 'chevron-down' : 'chevron-up'}
            size='large'
            color='gray'
            iconStyle='light'
          />
        )}
      </div>

      {!collapsed && (
        <>
          <Divider fitted />

          {/* Stats Row */}
          <div className={cx('content')}>
            <div className={cx('stats-row')}>
              <Statistic
                value={user.stats.total_purchases}
                label='Total Purchases'
                datatype='count'
                accentColor='info'
                size='compact'
              />
              <Statistic
                value={user.stats.participation_rate}
                label='Participation*'
                datatype='percentage'
                accentColor='success'
                size='compact'
              />
              <Statistic
                value={user.stats.active_registrations}
                label='Active Reg.'
                datatype='count'
                accentColor='purple'
                size='compact'
              />
              <Statistic
                value={user.stats.active_bids}
                label='Active Bids'
                datatype='count'
                accentColor='caution'
                size='compact'
              />
              <Statistic
                value={user.stats.active_closings}
                label='Active Closings'
                datatype='count'
                accentColor='gray'
                size='compact'
              />
            </div>

            {/* Info Row */}
            <div className={cx('info-row')}>
              {infoItems.map(item => (
                <LabelValue
                  level={4}
                  classNames={{ label: cx('label') }}
                  key={item.label}
                  label={item.label}
                  value={
                    <Text size='tiny' weight='medium' color='grayDark'>
                      {item.value}
                    </Text>
                  }
                />
              ))}
            </div>

            {/* Activity Sections - In-Person / Online */}
            <div className={cx('activity-grid')}>
              <ActivitySection
                icon='gavel'
                title='Live Auctions'
                rows={liveRows}
              />
              <ActivitySection
                icon='globe'
                title='Online Auctions'
                rows={onlineRows}
              />
            </div>

            {/* Bid to Close */}
            <div className={cx('full-width-section')}>
              <div className={cx('activity-header')}>
                <Icon name='clipboard' iconStyle='solid' color='gray' />
                <Text size='large' weight='medium' color='grayDark'>
                  Bid to Close
                </Text>
              </div>
              <Divider fitted className={cx('divider-accent')} />
              <div className={cx('activity-grid')}>
                <div>
                  {bidToCloseLeftRows.map(row => (
                    <div key={row.label} className={cx('activity-row')}>
                      <Text size='tiny' color='gray'>
                        {row.label}
                      </Text>
                      <Text size='tiny' weight='medium' color='grayDark'>
                        {row.value}
                      </Text>
                    </div>
                  ))}
                </div>
                <div>
                  {bidToCloseRightRows.map(row => (
                    <div key={row.label} className={cx('activity-row')}>
                      <Text size='tiny' color='gray'>
                        {row.label}
                      </Text>
                      <Text size='tiny' weight='medium' color='grayDark'>
                        {row.value}
                      </Text>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Remote Bid Funds */}
            <div className={cx('full-width-section')}>
              <div className={cx('activity-header')}>
                <Icon name='dollar-sign' iconStyle='solid' color='gray' />
                <Text size='large' weight='medium' color='grayDark'>
                  Remote Bid Funds
                </Text>
              </div>
              <Divider fitted className={cx('divider-accent')} />
              <div className={cx('activity-grid')}>
                <div>
                  {remoteBidLeftRows.map(row => (
                    <div key={row.label} className={cx('activity-row')}>
                      <Text size='tiny' color='gray'>
                        {row.label}
                      </Text>
                      <Text size='tiny' weight='medium' color='grayDark'>
                        {row.value}
                      </Text>
                    </div>
                  ))}
                </div>
                <div>
                  {remoteBidRightRows.map(row => (
                    <div key={row.label} className={cx('activity-row')}>
                      <Text size='tiny' color='gray'>
                        {row.label}
                      </Text>
                      <Text size='tiny' weight='medium' color='grayDark'>
                        {row.value}
                      </Text>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className={cx('footer')}>
            <Text size='mini' color='grayMedium'>
              * Values coming soon. (Not yet implemented.)
            </Text>
            <Button
              role='tertiary'
              color={coreConstants.ColorNames.Info}
              collapse
              icon
              elmId='user_profile_card_help_btn'
              onClick={() => setHelpOpen(true)}
              aria-label='Help'
            >
              <Icon
                name='circle-question'
                iconStyle='regular'
                size='large'
                color='inherit'
              />
            </Button>
          </div>

          <Modal
            open={helpOpen}
            title='User Profile Help'
            elmId='user_profile_card_help_modal'
            closeIcon={
              <Icon
                className='close'
                data-elm-id='user_profile_card_help_modal_close_icon'
                name='times'
                iconStyle='regular'
                fitted
                onClick={() => setHelpOpen(false)}
              />
            }
          >
            <Modal.Content className={cx('help-content')}>
              <MarkdownRenderer content={HELP_CONTENT} />
            </Modal.Content>
            <Modal.Actions>
              <Button
                role='primary'
                elmId='user_profile_card_help_modal_close_btn'
                onClick={() => setHelpOpen(false)}
              >
                Close
              </Button>
            </Modal.Actions>
          </Modal>
        </>
      )}
    </CardContainer>
  )
}

export default UserProfileCard
